// Content for the Help page (/help). Plain words for people who don't use
// apps much: one question per topic, short numbered steps, button names in
// **bold** exactly as they appear on screen.
//
// `others` is what a team leader or admin can do for you on the same topic.
// Members see it too, so they know who to ask; it isn't hidden by role.
// `audience` limits a whole section to team leaders (and deputies) or admins.

export type HelpAudience = 'everyone' | 'leader' | 'admin';

export interface HelpTopic {
  id: string;
  q: string;
  intro?: string;
  steps?: string[];
  tip?: string;
  others?: string;
}

export interface HelpSection {
  id: string;
  title: string;
  icon: string;
  audience: HelpAudience;
  topics: HelpTopic[];
}

/** Who can do what, shown to everyone at the top of the page. */
export const WHO_DOES_WHAT: { who: string; what: string[] }[] = [
  {
    who: 'Every member',
    what: [
      'See your shifts and who’s on',
      'Ask for cover, take covers and swap shifts',
      'Pick ops-support shifts when your team is on support',
      'Reply to activities and give scratch-week availability',
      'Do vehicle and equipment checks',
    ],
  },
  {
    who: 'Team leaders and deputies',
    what: [
      'Everything a member can do, plus:',
      'Add people to their team',
      'Put team members on leave',
      'Ask for cover for a team member who doesn’t use the app',
      'Assign ops-support shifts nobody picked',
    ],
  },
  {
    who: 'Admins',
    what: [
      'Everything above, for the whole unit, plus:',
      'Build the roster: duty team, support team and UDOs each week',
      'Add members and send app invites',
      'Keep qualifications, levels and positions up to date',
      'Create activities and build scratch-week crews',
      'Set up vehicles, equipment and check schedules',
    ],
  },
];

export const HELP_SECTIONS: HelpSection[] = [
  {
    id: 'start',
    title: 'Getting started',
    icon: 'home',
    audience: 'everyone',
    topics: [
      {
        id: 'install',
        q: 'How do I put MusterTap on my phone?',
        intro: 'MusterTap runs in your web browser, but it works best once it’s on your home screen, like any other app.',
        steps: [
          '**iPhone:** open mustertap.com in **Safari**, tap the **Share** button (square with an arrow), then **Add to Home Screen**.',
          '**Android:** open mustertap.com in **Chrome**, tap the **⋮** menu, then **Install app** (or **Add to Home screen**).',
          'From now on, open MusterTap from the new icon on your home screen.',
        ],
        tip: 'On an iPhone, notifications only work once MusterTap is on your home screen.',
      },
      {
        id: 'notifications',
        q: 'How do I get notifications?',
        intro: 'Notifications tell you when someone needs cover, when your request is taken, and when something needs you.',
        steps: [
          'On **Home**, tap your initials in the top right to open **Profile**.',
          'In **Notifications**, turn them on and tap **Allow** when your phone asks.',
          'Tap **Send a test notification** to check it works.',
        ],
        tip: 'Notifications are set up per phone or computer. Do this on each device you use. Rather have email? Choose **Email me instead**. Don’t want them at night? Set **Quiet hours** in Profile.',
      },
      {
        id: 'quiet-hours',
        q: 'Can I stop notifications at night or on weekends?',
        steps: [
          'Open **Profile** and find **Quiet hours**.',
          'Turn on **Weekdays**, **Weekends** or **Public holidays**, and set the times (for example 22:00 to 07:00), or tick **All day**.',
          'Tap **Save quiet hours**.',
        ],
        tip: 'Nothing is lost: what arrives during quiet time comes as one catch-up when it ends. **Let urgent ones through** (on by default) still lets a vehicle off run, a short crew, or someone asking you directly for the next 12 hours reach you.',
      },
      {
        id: 'home',
        q: 'What’s on the Home screen?',
        steps: [
          '**Needs you:** things waiting on you, like a cover you could take or a swap to answer. Tap a line to deal with it. If there’s nothing, it isn’t shown.',
          '**Your next shift:** your next rostered shifts.',
          '**On now:** who’s on duty right now, including the UDO, and any vehicle that’s off run.',
          '**This week:** the duty team, support team and UDOs for this week.',
          '**Checks & faults:** your team’s checks for this week, and how many faults are open or vehicles off run.',
        ],
        tip: 'Tap any card to see more. The buttons along the bottom (Home, Shifts, Checks) take you to the main areas.',
      },
      {
        id: 'sign-in',
        q: 'I can’t sign in',
        steps: [
          'On the sign-in page, tap **Forgot password?**',
          'Enter your email. We’ll email you a code.',
          'Enter the code and choose a new password (at least 10 characters).',
        ],
        others: 'If the email never arrives, or you’ve lost access to your email, ask an admin. They can fix your email address, reset your sign-in or send you a new invite link.',
      },
      {
        id: 'units',
        q: 'I’m in more than one unit. How do I switch?',
        steps: [
          'On **Home**, tap the unit name in the top left.',
          'Pick the unit you want. MusterTap shows one unit at a time.',
        ],
      },
    ],
  },
  {
    id: 'shifts',
    title: 'Shifts and cover',
    icon: 'calendar',
    audience: 'everyone',
    topics: [
      {
        id: 'my-shifts',
        q: 'When am I rostered on?',
        steps: [
          'Tap **Shifts** at the bottom, then **Mine**.',
          'You’ll see every shift you’re rostered for in the coming weeks: duty crew, UDO, ops support, covers you’ve taken and activities you’re going to.',
        ],
        tip: 'If you’re rostered for two things at the same time (say, duty crew and UDO), you’ll see a warning. Tap **Can’t make it** on one of them to ask for cover.',
        others: 'Admins build the roster. If a shift looks wrong, ask an admin to check it.',
      },
      {
        id: 'calendar',
        q: 'Can I see my shifts in my phone’s calendar?',
        steps: [
          'Open **Profile** and find **Add shifts to your calendar**. Tap **Set up**.',
          'Tap **Google**, **Apple / iPhone** or **Outlook**, and confirm in your calendar app. Or tap **Copy link** and add it as a calendar “from URL” or “subscription”.',
        ],
        tip: 'It keeps itself up to date with your shifts, covers you’ve taken and activities you’re going to. Calendar apps check every few hours (Google can take up to a day), so look in MusterTap for last-minute changes. The link is private — if you shared it by mistake, make a new one.',
      },
      {
        id: 'shift-reminders',
        q: 'Can I get a reminder before my shift?',
        steps: [
          'Open **Profile**. In **Notifications**, choose **Remind me before my shifts**: 1, 3, 12 or 24 hours before, or Off.',
        ],
        tip: 'It’s on at 12 hours unless you change it, so a 6am shift is reminded at about 6pm the evening before. If the reminder would land in your quiet hours, it comes just before they start instead.',
      },
      {
        id: 'whos-on',
        q: 'Who’s on duty?',
        steps: [
          'For **right now**, look at **On now** on Home. Tap it for the full picture: vehicles off run, UDO, crew, ops support and any gaps.',
          'For **any week**, tap **Shifts**, then **Who’s on**. Use the arrows to move between weeks.',
        ],
        tip: 'Everyone in the unit can see gaps and who’s unavailable. That way a missing crew member is spotted before a job, not during one.',
      },
      {
        id: 'cant-make-it',
        q: 'I can’t make my shift. How do I ask for cover?',
        steps: [
          'Tap **Shifts**, then **Mine**.',
          'Find the shift and tap **Can’t make it**.',
          'Choose **Whole shift**, or **Just some hours** if you can do part of it.',
          'Check **If nobody covers**. It shows what the crew would be short of, for example no truck driver.',
          'Add a note if you like, then send it.',
        ],
        tip: 'Everyone who is free and able to cover gets a notification (people in their quiet hours see it when those end). Your request stays open until the shift starts. If nobody takes it, you show as a gap.',
        others: 'Finding cover is up to you, but you’re not on your own: your team leader and the UDO can see open requests and gaps. A team leader or admin can also ask for cover for a member who doesn’t use the app.',
      },
      {
        id: 'nudge',
        q: 'Nobody has taken my cover. Can I ask someone directly?',
        steps: [
          'Open your request. It’s on **Shifts → Cover** under **Your cover requests**.',
          'Under **Ask someone directly**, the people who are free are listed, best match first.',
          'Tap **Nudge** next to someone to send them a personal notification.',
        ],
        tip: 'For a shift in the next 12 hours, a nudge counts as urgent and reaches them even in their quiet hours (unless they’ve turned that off).',
      },
      {
        id: 'cancel-cover',
        q: 'I can make my shift after all',
        steps: [
          'Open your request on **Shifts → Cover**.',
          'Cancel it. You’re back on the shift.',
        ],
      },
      {
        id: 'take-cover',
        q: 'How do I cover someone else’s shift?',
        steps: [
          'On Home, tap **cover requests you could take** under **Needs you**. Or go to **Shifts → Cover**.',
          'Open a request to see the shift, the crew and what they’re short of.',
          'Tap **I’ll cover all of it**, or pick the hours you can do and tap **Cover these hours**.',
        ],
        tip: 'You can’t take a cover that clashes with something you’re already rostered for. The app will tell you why.',
      },
      {
        id: 'withdraw-cover',
        q: 'I took a cover but now I can’t do it',
        steps: [
          'Open the cover from **Shifts → Mine** (tap **Details**).',
          'Tap **I can’t do it any more**. You can do this until the shift starts.',
        ],
        tip: 'The person you were covering is told, and the shift opens up again for others.',
      },
      {
        id: 'swap',
        q: 'How do I swap a shift with someone?',
        intro: 'A swap is “I’ll do yours if you do mine”. It works for any kind of shift.',
        steps: [
          'Tap **Shifts → Mine**, find your shift and tap **Swap**.',
          'Under **Swap with**, choose the person and which of their shifts you’ll take.',
          'Tap **Send swap offer**.',
          'They’ll get a notification and can **Accept swap** or **Decline**. If your shift starts within 12 hours, it reaches them even in their quiet hours.',
        ],
        tip: 'Both halves happen together or not at all, so nobody ends up with two shifts. Changed your mind before they answer? Open the swap and tap **Withdraw offer**.',
      },
    ],
  },
  {
    id: 'ops',
    title: 'Ops support',
    icon: 'people',
    audience: 'everyone',
    topics: [
      {
        id: 'ops-pick',
        q: 'My team is on ops support. How do I pick my shifts?',
        steps: [
          'When your team is the support team, Home shows **Ops support, week of …** under **Needs you**. Tap it.',
          'Tap **Take** on the shifts that suit you. Tap **Release** to give one back.',
          'Pick before the cut-off: **6pm on the Thursday** before the week starts.',
        ],
        tip: 'Always do the same night? Set **My fixed days** on that page and tap **Save fixed days**. You’ll be put on that shift automatically every time your team is on support.',
        others: 'After the Thursday cut-off, your team leader or deputy assigns whatever’s left. The app suggests whoever has done the fewest of that kind of shift. Everyone can see how many weeknight, weekend-night and weekend-day shifts each person has done.',
      },
    ],
  },
  {
    id: 'activities',
    title: 'Activities and scratch weeks',
    icon: 'flag',
    audience: 'everyone',
    topics: [
      {
        id: 'activities',
        q: 'How do I reply to training nights and other activities?',
        steps: [
          'Tap **Shifts**, then **Activities**. If something needs a reply, it also shows under **Needs you** on Home.',
          'Open the activity and choose **Going**, **Maybe** or **Can’t**. If it asks for helpers, tap **Sign me up**.',
        ],
        others: 'Admins create activities: training nights, power maintenance, skills sessions and community engagement. They can see who’s coming.',
      },
      {
        id: 'scratch',
        q: 'How do I give my availability for the Christmas scratch weeks?',
        intro: 'In scratch weeks there are no regular teams. Crews are built from who’s available.',
        steps: [
          'On Home, tap **give your availability** under **Needs you**.',
          'Mark the days and nights you can do, then save.',
          'Reply before the date shown, so you’re included.',
        ],
        others: 'Admins build the crews from everyone’s replies and spread the load. Once they publish, your shifts appear under **Shifts → Mine**, and you can ask for cover or swap them as usual.',
      },
    ],
  },
  {
    id: 'leave',
    title: 'Leave',
    icon: 'clock',
    audience: 'everyone',
    topics: [
      {
        id: 'leave',
        q: 'I’m going away or need a break. What do I do?',
        intro: 'You can’t put yourself on leave in the app. Tell your team leader or an admin, with the dates.',
        steps: [
          'They put you on leave for those dates. If you don’t know your return date, they can leave it open.',
          'While you’re on leave you aren’t counted on the crew, you aren’t asked to cover, and you don’t get notifications.',
          'Any ops-support shifts you’d picked for those dates are released for others.',
        ],
        tip: 'If you give a reason, only admins and your own team leaders can see it.',
        others: 'Team leaders and deputies can put members of their team on leave. Admins can put anyone on leave.',
      },
    ],
  },
  {
    id: 'checks',
    title: 'Vehicle and equipment checks',
    icon: 'list',
    audience: 'everyone',
    topics: [
      {
        id: 'do-check',
        q: 'How do I do a check?',
        steps: [
          'Tap **Checks** at the bottom. Your team’s checks for this week are at the top.',
          'Open a check, or tap **Start a check** and choose the vehicle or equipment.',
          'Pick your area to start. Go through each item: tap **OK** if it’s fine and it moves straight to the next one.',
          'If something is wrong, choose **Missing**, **Broken** or **Attention**, and add a note or photo.',
          'When you’re done, tap **Review & submit**. Anything wrong goes into **Faults**, and anything found fine again is closed there.',
        ],
        tip: 'Several people can work on the same check at once, each in their own area. Items already in the fault register are marked, so check whether they’re still a problem. Fixed something on the spot? Choose **Rectified** and say what you did. No signal? Keep going — see the next question.',
        others: 'Admins decide what gets checked, how often, and which team does it each week.',
      },
      {
        id: 'photo-markup',
        q: 'How do I show exactly what’s wrong in a photo?',
        steps: [
          'On the item, tap **Add** under Photos and take the picture.',
          'Before it’s saved you can mark it up: **Circle** or **Arrow** the problem, **Draw**, or add a **Text** label like “leak here”. Pick a colour that stands out; **Undo** takes back the last mark.',
          'Tap **Done** to save the marked-up photo, or **Use as is** to skip.',
        ],
        tip: 'The photo as taken is kept too: tap a photo to see it large, then **Show original**.',
      },
      {
        id: 'offline-check',
        q: 'There’s no signal in the shed. Can I still do the check?',
        steps: [
          'Open the check once where there’s signal (at home, in the car park) — after that it’s on your phone.',
          'Go through it as usual. Every tap and photo is saved on your phone, and a bar at the top says how many changes are waiting.',
          'When you’re back in signal they send by themselves. **Review & submit** works once everything has been sent.',
        ],
        tip: 'If someone else changed the same item while you were offline, you’ll see it at the top: keep theirs, or tap **Use mine**. Starting a brand-new check needs signal. Don’t sign out while changes are waiting — they’d be lost.',
      },
      {
        id: 'report-fault',
        q: 'Something is broken or missing between checks. How do I report it?',
        steps: [
          'On Home, tap the **open faults** line under **Checks & faults**, then **Report**.',
          'Pick the vehicle, then the equipment (or “the vehicle itself” for things like tyres or lights), and say what’s wrong.',
          'If it shouldn’t be used, tick **It shouldn’t be used**. Admins are told straight away.',
        ],
        tip: 'The next check of that vehicle shows it, so the crew knows to look.',
        others: 'Admins can assign a fault to someone and take a vehicle off run.',
      },
      {
        id: 'fix-issue',
        q: 'I fixed something that was reported. How do I record it?',
        steps: [
          'Open the fault from **Faults** (or the item from the check it was found in).',
          'Tap **Mark as fixed** and say what was done, for example “Replaced with spare from store”.',
        ],
        tip: 'Add a note along the way, for example “parts ordered”, so everyone can see where it’s up to. If the next check finds it fine, it closes by itself.',
        others: 'If something was marked fixed by mistake, an admin can reopen it.',
      },
      {
        id: 'off-run',
        q: 'How do I know if a vehicle is off run?',
        steps: [
          'Off-run vehicles show in red on Home (under **On now**), on the **On now** board and at the top of **Faults**.',
          'Everyone gets a notification when a vehicle goes off run (even in quiet hours, unless they’ve turned urgent ones off) and when it’s back on.',
          'If your unit’s primary response vehicle is off run, the notification and the **On now** board say which vehicle to use instead.',
        ],
        others: 'Only admins take a vehicle off run or put it back on, and choose the primary response vehicle (on the vehicle’s page, under **Edit**). The next one to use follows the order of their vehicle list.',
      },
      {
        id: 'find-gear',
        q: 'Where is a piece of equipment?',
        steps: [
          'On Home, tap the magnifying glass at the top.',
          'Type what you’re looking for. You’ll see which vehicle or location it’s on, and any expiry date.',
        ],
      },
    ],
  },
  {
    id: 'me',
    title: 'Your details',
    icon: 'shield',
    audience: 'everyone',
    topics: [
      {
        id: 'my-quals',
        q: 'My qualifications or team are wrong',
        steps: [
          'Open **Profile** (your initials on Home). Your team, level, qualifications and positions are shown there.',
        ],
        tip: 'If a qualification has an expiry date (like EVR), you get a reminder 30 days and 7 days before, and on the day. Once it’s renewed, tell an admin the new date.',
        others: 'Only admins can change qualifications, levels and positions. Tell an admin what needs fixing. This matters because cover suggestions are based on qualifications.',
      },
      {
        id: 'my-account',
        q: 'How do I change my name, password or dark mode?',
        steps: [
          'Open **Profile**.',
          'Tap your name to change it. Tap **Change password** to choose a new one.',
          'Under **Appearance**, choose **Light**, **Dark** or **System**.',
        ],
      },
    ],
  },
  {
    id: 'leaders',
    title: 'For team leaders and deputies',
    icon: 'people',
    audience: 'leader',
    topics: [
      {
        id: 'tl-team',
        q: 'Where do I manage my team?',
        steps: [
          'Tap **Admin** at the bottom. As a team leader you’ll only see **Teams**.',
          'Here you can add people to your team with **Add member…**',
        ],
        tip: 'Your **Your team** card on Home shows who in your team is on leave or needs attention.',
      },
      {
        id: 'tl-leave',
        q: 'How do I put someone in my team on leave?',
        steps: [
          'Go to **Admin → Teams** and open the member.',
          'Tap **Put on leave**, set the dates (or leave the end open), and save.',
        ],
      },
      {
        id: 'tl-cover-for',
        q: 'A member doesn’t use the app. How do I ask for cover for them?',
        steps: [
          'Tap **Shifts → Mine**.',
          'Under **Showing**, choose the member.',
          'Tap **Can’t make it** on their shift and send the request as usual.',
        ],
        tip: 'You get the updates on their behalf, and Home reminds you while the request is still open. Under their shifts there’s also a calendar link you can send them, so their shifts show in their own calendar.',
      },
      {
        id: 'tl-ops',
        q: 'Nobody picked some ops-support shifts. How do I fill them?',
        steps: [
          'After the Thursday 6pm cut-off, open that week’s ops support from Home or **Shifts → Mine**.',
          'Each empty shift shows a suggested person. Tap **Assign**.',
        ],
      },
    ],
  },
  {
    id: 'admins',
    title: 'For admins',
    icon: 'shield',
    audience: 'admin',
    topics: [
      {
        id: 'ad-roster',
        q: 'How do I set up the roster?',
        steps: [
          'Go to **Admin → Roster**.',
          'For each week, set the duty team, the support team and the UDO.',
          'Set the **Default day UDO**. It fills every week unless you change a week.',
          'Check **Public holidays**. Add local ones if needed.',
        ],
        tip: 'Shifts are created up to 8 weeks ahead. A week without a duty team has no shifts.',
      },
      {
        id: 'ad-members',
        q: 'How do I add members and invite them to the app?',
        steps: [
          'Go to **Admin → Members** and tap **Add member**. Add one person, or use **Paste a list**.',
          'Members don’t need the app to be on the roster. To give someone the app, tap **Invite to app**, then **Create invite** and **Copy link**.',
          'Send them the link however suits you (text or email).',
        ],
        tip: 'A member marked **Email bounced** or **Marked spam** needs their email fixed.',
      },
      {
        id: 'ad-quals',
        q: 'How do I update qualifications?',
        steps: [
          'Open the member in **Admin → Members** to set their level, operational status, UDO, qualifications (with expiry dates) and positions.',
          'To change the list of qualifications and positions for the unit, go to **Admin → Quals & positions**.',
        ],
      },
      {
        id: 'ad-reports',
        q: 'How do I get a report on how the unit is going?',
        steps: [
          'Go to **Admin → Reports** and pick a period: this or last month, a quarter, the financial year, or your own dates.',
          'It shows checks done on time, faults, vehicles off run, short crews, cover, ops support, leave and expiring qualifications.',
          'Tap **PDF** to save or print it, **CSV** for Excel, or **Email it to me**.',
        ],
        tip: 'Every admin also gets last month’s report by email on the 1st. Don’t want it? Turn off **Monthly unit report** in Profile → Notifications.',
      },
      {
        id: 'ad-faults',
        q: 'How do I manage faults and take a vehicle off run?',
        steps: [
          'Open **Faults** from Home. New faults from a check come to you as one notification per check.',
          'Open a fault to assign it to someone (they’re notified), add a work-order reference, or mark it fixed.',
          'Tap **Take … off run** on the fault (or on the asset in **Admin → Assets**), say why and, if you know it, when it’s expected back. When it’s ready, tap **Back on run**.',
        ],
        tip: 'Expiry dates on equipment and qualifications send reminders 30 days and 7 days before, and on the day. Admins get one summary; members get their own qualification reminders.',
      },
      {
        id: 'ad-activities',
        q: 'How do I create an activity?',
        steps: [
          'Tap **Shifts → Activities**, then **New activity**.',
          'Fill in the title, date and time, and how people reply: going / maybe / can’t, or sign up with the number of people needed.',
        ],
      },
      {
        id: 'ad-scratch',
        q: 'How do I run the scratch weeks?',
        steps: [
          'Go to **Admin → Roster**, find **Scratch weeks**, and tap **Ask for availability**. Set the reply-by date.',
          'Once people have replied, open **Build roster**. **Suggest (fill empty spots)** does a first pass for you.',
          'Adjust, then **Publish roster**. Later edits need **Publish changes**.',
        ],
      },
      {
        id: 'ad-checks',
        q: 'How do I set up vehicles, equipment and check schedules?',
        steps: [
          '**Assets**: vehicles, trailers and sheds, and what’s checked on each.',
          '**Equipment** and **Locations**: gear and where it lives.',
          '**Schedules**: how often each check is due and which team does it.',
          '**Activity**: a log of who changed what.',
        ],
      },
    ],
  },
];
